import { useState, useEffect } from 'react';
import { Phone, MessageCircle, Mail } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle, DrawerTrigger } from '@/components/ui/drawer';
import { ProjectContactForm } from './ProjectContactForm';

interface MobileBottomCTAProps {
  projectId: string;
  projectName?: string;
  referenceCode?: string;
  phone?: string;
  whatsapp?: string;
  assignedAgentId?: string | null;
}

export function MobileBottomCTA({ projectId, projectName, referenceCode, phone = '+251', whatsapp = '251', assignedAgentId }: MobileBottomCTAProps) {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const handler = () => setOpen(true);
    window.addEventListener('open-project-inquiry', handler);
    return () => window.removeEventListener('open-project-inquiry', handler);
  }, []);

  return (
    <div className="lg:hidden fixed bottom-0 left-0 right-0 z-30 bg-background border-t shadow-[0_-2px_10px_rgba(0,0,0,0.05)]">
      <div className="flex items-center gap-2 px-3 py-2.5 safe-area-pb">
        <Drawer open={open} onOpenChange={setOpen}>
          <DrawerTrigger asChild>
            <Button variant="outline" className="flex-1 h-11 font-bold text-primary border-primary/40 bg-primary/5 hover:bg-primary/10 gap-1.5 text-sm">
              <Mail className="h-4 w-4" /> Get More Info
            </Button>
          </DrawerTrigger>
          <DrawerContent className="max-h-[90vh]">
            <DrawerHeader>
              <DrawerTitle>Inquire About This Project</DrawerTitle>
            </DrawerHeader>
            <div className="px-4 pb-6 overflow-y-auto">
              <ProjectContactForm projectId={projectId} projectName={projectName} referenceCode={referenceCode} assignedAgentId={assignedAgentId} onSuccess={() => setOpen(false)} />
            </div>
          </DrawerContent>
        </Drawer>
        <Button asChild className="h-11 px-5 font-bold gap-1.5 text-sm">
          <a href={`tel:${phone}`}>
            <Phone className="h-4 w-4" /> Call
          </a>
        </Button>
        <Button asChild variant="outline" className="h-11 w-11 p-0 border-[hsl(142,70%,45%)]/40 hover:bg-[hsl(142,70%,45%)]/10">
          <a
            href={`https://wa.me/${whatsapp}?text=${encodeURIComponent(`Hi, I'd like to inquire about ${projectName || 'this project'}${referenceCode ? ` (${referenceCode.toUpperCase()})` : ''}. Please share availability, pricing and next steps.`)}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            <MessageCircle className="h-5 w-5 text-[hsl(142,70%,45%)]" />
          </a>
        </Button>
      </div>
    </div>
  );
}
